import { EnhancedTokenManager } from '../utils/tokenManager';
import { SubdomainManager } from '../utils/subdomainUtils';
import { BASE_URL } from '../api/urls';

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraProjectUser {
  account_id: string;
  name: string;
  email?: string;
  active: boolean;
}

export interface JiraIntegrationStatus {
  isConnected: boolean;
  hasDefaultProject: boolean;
  defaultProject?: {
    id: string;
    key: string;
    name?: string;
  };
}

export interface IntegrationToggleResponse {
  success: boolean;
  user_id: number;
  platform: string;
  is_enabled: boolean;
  message: string;
}

export class JiraIntegrationService {
  private static POPUP_WIDTH = 600;
  private static POPUP_HEIGHT = 700;
  private static INTEGRATION_CHECK_KEY = 'jira_integration_pending';

  static async toggleIntegration(isEnabled: boolean): Promise<{
    success: boolean;
    data?: IntegrationToggleResponse;
    error?: string;
  }> {
    try {
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/integrations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform: 'jira', is_enabled: isEnabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to toggle integration: ${response.status}`);
      }

      const data: IntegrationToggleResponse = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error toggling Jira integration:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle integration',
      };
    }
  }

  static async enableIntegration(): Promise<{
    success: boolean;
    needsAuth?: boolean;
    error?: string;
  }> {
    try {
      const toggleResult = await this.toggleIntegration(true);
      if (!toggleResult.success) {
        return { success: false, error: toggleResult.error || 'Failed to enable integration' };
      }
      try {
        await this.getProjects();
        return { success: true, needsAuth: false };
      } catch {
        return { success: true, needsAuth: true };
      }
    } catch (error) {
      console.error('Error enabling Jira integration:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to enable integration',
      };
    }
  }

  static async disableIntegration(): Promise<{ success: boolean; error?: string }> {
    try {
      const toggleResult = await this.toggleIntegration(false);
      if (!toggleResult.success) {
        return { success: false, error: toggleResult.error || 'Failed to disable integration' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error disabling Jira integration:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to disable integration',
      };
    }
  }

  static async connectJira(): Promise<{ success: boolean; error?: string }> {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) throw new Error('User not authenticated');

      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/oauth/jira/url`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get OAuth URL from backend');
      }

      const data = await response.json();
      const oauthUrl = data.url;
      if (!oauthUrl) throw new Error('Backend did not return OAuth URL');

      const currentSubdomain = SubdomainManager.getCurrentSubdomain();
      const returnPath = '/dashboard/settings/integration';
      if (currentSubdomain) localStorage.setItem('jira_oauth_return_subdomain', currentSubdomain);
      localStorage.setItem('jira_oauth_return_path', returnPath);
      localStorage.setItem(this.INTEGRATION_CHECK_KEY, 'true');

      const left = window.screen.width / 2 - this.POPUP_WIDTH / 2;
      const top = window.screen.height / 2 - this.POPUP_HEIGHT / 2;
      const popup = window.open(
        oauthUrl,
        'jira_oauth',
        `width=${this.POPUP_WIDTH},height=${this.POPUP_HEIGHT},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) throw new Error('Popup blocked. Please allow popups for this site.');

      return new Promise((resolve) => {
        let statusCheckInterval: NodeJS.Timeout | null = null;
        let popupCheckInterval: NodeJS.Timeout | null = null;
        let messageListener: ((event: MessageEvent) => void) | null = null;
        let hasResolved = false;

        const cleanup = () => {
          if (statusCheckInterval !== null) { clearInterval(statusCheckInterval); statusCheckInterval = null; }
          if (popupCheckInterval !== null) { clearInterval(popupCheckInterval); popupCheckInterval = null; }
          if (messageListener) { window.removeEventListener('message', messageListener); messageListener = null; }
        };

        const resolveSuccess = () => {
          if (hasResolved) return;
          hasResolved = true;
          localStorage.setItem(this.INTEGRATION_CHECK_KEY, 'success');
          if (popup && !popup.closed) { try { popup.close(); } catch {} }
          cleanup();
          resolve({ success: true });
        };

        const resolveError = (error: string) => {
          if (hasResolved) return;
          hasResolved = true;
          localStorage.removeItem(this.INTEGRATION_CHECK_KEY);
          cleanup();
          resolve({ success: false, error });
        };

        messageListener = (event: MessageEvent) => {
          if (event.data?.type === 'JIRA_OAUTH_COMPLETE' && event.data.success) {
            setTimeout(() => resolveSuccess(), 500);
          }
        };
        window.addEventListener('message', messageListener);

        const checkBackendStatus = async () => {
          try {
            const status = await this.checkIntegrationStatus();
            if (status.isConnected) resolveSuccess();
          } catch {}
        };

        statusCheckInterval = setInterval(checkBackendStatus, 2000);
        checkBackendStatus();

        popupCheckInterval = setInterval(() => {
          try {
            if (popup.closed) {
              if (popupCheckInterval !== null) { clearInterval(popupCheckInterval); popupCheckInterval = null; }
              if (!hasResolved) {
                setTimeout(() => {
                  if (hasResolved) return;
                  const integrationStatus = localStorage.getItem(this.INTEGRATION_CHECK_KEY);
                  if (integrationStatus === 'success') resolveSuccess();
                  else if (integrationStatus === 'error') resolveError('Jira connection failed. Please try again.');
                  else checkBackendStatus().then(() => { if (!hasResolved) resolveError('Authentication cancelled'); });
                }, 1000);
              }
            }
          } catch {}
        }, 500);

        setTimeout(() => {
          if (hasResolved) return;
          if (!popup.closed) popup.close();
          resolveError('Authentication timeout');
        }, 300000);
      });
    } catch (error) {
      console.error('Error connecting to Jira:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect to Jira',
      };
    }
  }

  static async checkIntegrationStatus(): Promise<JiraIntegrationStatus> {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) return { isConnected: false, hasDefaultProject: false };

      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/integrations/status`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) return { isConnected: false, hasDefaultProject: false };

      const data = await response.json();
      return {
        isConnected: data.jira_connected === true,
        hasDefaultProject: false,
        defaultProject: data.jira_cloud_id ? { id: data.jira_cloud_id, key: '', name: '' } : undefined,
      };
    } catch {
      return { isConnected: false, hasDefaultProject: false };
    }
  }

  static async getProjects(): Promise<JiraProject[]> {
    try {
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/jira/projects`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        if (response.status === 404 || response.status === 401) return [];
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching Jira projects:', error);
      throw error;
    }
  }

  static async getProjectUsers(projectKey: string): Promise<JiraProjectUser[]> {
    try {
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/jira/projects/${projectKey}/users`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Failed to fetch project users: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching Jira project users:', error);
      throw error;
    }
  }

  static async setDefaultProject(projectId: string, projectKey: string): Promise<boolean> {
    try {
      const formData = new URLSearchParams();
      formData.append('project_id', projectId);
      formData.append('project_key', projectKey);
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/jira/default_project`,
        { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() }
      );
      if (!response.ok) throw new Error(`Failed to set default project: ${response.status}`);
      return true;
    } catch (error) {
      console.error('Error setting default project:', error);
      throw error;
    }
  }

  static async pushFeedbackToDefault(feedbackId: number): Promise<{
    success: boolean;
    jiraIssue?: string;
    error?: string;
  }> {
    try {
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/${feedbackId}/push/jira`,
        { method: 'GET' }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to push feedback: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, jiraIssue: data.jira_issue };
    } catch (error) {
      console.error('Error pushing feedback to Jira:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to push feedback' };
    }
  }

  static async pushFeedbackToCustom(
    feedbackId: number,
    projectKey: string,
    assigneeAccountId?: string
  ): Promise<{
    success: boolean;
    jiraIssueKey?: string;
    project?: string;
    assigneeAccountId?: string;
    finalStatus?: string;
    message?: string;
    error?: string;
  }> {
    try {
      if (!feedbackId || isNaN(feedbackId)) throw new Error('Invalid feedback ID');
      if (!projectKey) throw new Error('Project key is required');

      const params = new URLSearchParams();
      params.append('project_key', projectKey);
      if (assigneeAccountId) params.append('assignee_account_id', assigneeAccountId);

      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/${feedbackId}/push/jira/?${params.toString()}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        jiraIssueKey: data.jira_issue_key,
        project: data.project,
        assigneeAccountId: data.assignee_account_id,
        finalStatus: data.final_status,
        message: data.message,
      };
    } catch (error) {
      console.error('Error pushing feedback to Jira:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to push feedback' };
    }
  }

  static async disconnect(): Promise<boolean> {
    try {
      const response = await EnhancedTokenManager.fetchWithAutoRefresh(
        `${BASE_URL}/feedback/jira/disconnect`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error(`Failed to disconnect: ${response.status}`);
      return true;
    } catch (error) {
      console.error('Error disconnecting Jira:', error);
      throw error;
    }
  }

  static handleOAuthCallback(success: boolean) {
    localStorage.setItem(this.INTEGRATION_CHECK_KEY, success ? 'success' : 'error');
    const returnPath = localStorage.getItem('jira_oauth_return_path') || '/dashboard/settings/integration';
    const returnSubdomain = localStorage.getItem('jira_oauth_return_subdomain');
    localStorage.removeItem('jira_oauth_return_path');
    localStorage.removeItem('jira_oauth_return_subdomain');
    if (returnSubdomain && !SubdomainManager.isLocalDevelopment()) {
      SubdomainManager.navigateToSubdomain(returnSubdomain, returnPath, false);
    } else {
      window.location.href = returnPath;
    }
  }
}
